/* eslint-disable */
// AuthContext — admin JWT auth for Larinz Labz.
//
// Defense-in-depth: backend sets an httpOnly cookie on /auth/login (works for
// same-origin tooling / curl-with-cookies). The SPA primarily uses the Bearer
// token in localStorage so it stays compatible with the K8s ingress wildcard
// CORS (which forbids credentials).

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const AuthCtx = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'll-admin-token';
// sessionStorage: cleared on tab/window close, not shared across tabs.
// The backend also sets an httpOnly cookie as a defence-in-depth layer.
const store = {
  get: () => sessionStorage.getItem(STORAGE_KEY),
  set: (v) => sessionStorage.setItem(STORAGE_KEY, v),
  del: () => sessionStorage.removeItem(STORAGE_KEY),
};

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => store.get() || null);
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState(null);

  // Verify session on mount / token change using the Bearer header.
  useEffect(() => {
    let cancelled = false;
    if (!token) { setUser(null); setChecking(false); return; }
    (async () => {
      try {
        const r = await fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) throw new Error('me-failed');
        const u = await r.json();
        if (!cancelled) { setUser(u); setChecking(false); }
      } catch (e) {
        if (!cancelled) {
          console.warn('Auth check failed:', e);
          store.del();
          setToken(null); setUser(null); setChecking(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const login = useCallback(async (username, password) => {
    setError(null);
    try {
      const r = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (!r.ok) {
        let detail = 'Login failed';
        try { detail = (await r.json()).detail || detail; }
        catch (e) { console.warn('login error parse failed:', e); }
        setError(typeof detail === 'string' ? detail : 'Login failed');
        return false;
      }
      const data = await r.json();
      store.set(data.access_token);
      setToken(data.access_token);
      setUser(data.user);
      return true;
    } catch (e) {
      console.error('Login network failed:', e);
      setError('Network error — please try again');
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    // Best-effort cookie clear (fire-and-forget; no credentials needed since
    // backend's delete_cookie sends an unconditional Set-Cookie).
    try { await fetch(`${API}/auth/logout`, { method: 'POST' }); }
    catch (e) { console.warn('Logout request failed:', e); }
    store.del();
    setToken(null); setUser(null);
  }, []);

  const value = useMemo(
    () => ({ token, user, checking, error, login, logout, API }),
    [token, user, checking, error, login, logout]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
